import { Fragment, type FC } from 'react';

import EligibilityCriteriaBox from '@/components/EligibilityCriteriaBox';
import DetailField from '@/components/TreatmentGroupDetailSections/components/DetailField';
import SessionCard from '@/components/TreatmentGroupDetailSections/components/SessionCard';
import SessionRow from '@/components/TreatmentGroupDetailSections/components/SessionRow';
import type { Props } from '@/components/TreatmentGroupDetailSections/types';
import TreatmentGroupMembersList from '@/components/TreatmentGroupMembersList';
import UpcomingAppointmentsList from '@/components/UpcomingAppointmentsList';
import useTreatmentGroupJurisdictionNames from '@/hooks/useTreatmentGroupJurisdictionNames';
import { TreatmentGroupSession } from '@/types/treatmentGroup';
import { Card, Pill, Typography } from '@/ui';
import {
  daysUntil,
  formatCalendarDate,
  formatOptionalCalendarDate,
  getNextMonday
} from '@/utils/dates';
import { isJurisdictionGroup } from '@/utils/jurisdictionEligibility';
import { groupHasStarted } from '@/utils/treatmentGroupSessions';

const emptyDash = (value: string) => value || '—';

const isEndedSession = (session: TreatmentGroupSession) =>
  !!session.effective_until && daysUntil(`${session.effective_until}T00:00:00`) <= 0;

const isEndingSoonSession = (session: TreatmentGroupSession) =>
  !!session.effective_until && !isEndedSession(session);

// Not-yet-started only counts as a *pending* addition when the group
// itself has already started -- otherwise this session is simply part of
// the group's not-yet-live initial schedule, which should still show as
// current rather than as an upcoming change.
const isPendingAddSession = (session: TreatmentGroupSession, group: Props['group']) =>
  !group.is_draft &&
  groupHasStarted(group) &&
  !session.effective_until &&
  daysUntil(`${session.effective_from}T00:00:00`) > 0;

const TreatmentGroupDetailSections: FC<Props> = ({
  group,
  memberActions,
  memberFirstName,
  timezone
}) => {
  const jurisdictionNames = useTreatmentGroupJurisdictionNames(group.id);

  const endedSessions = group.sessions.filter(isEndedSession);
  const pendingRemoveSessions = group.sessions.filter(isEndingSoonSession);
  const pendingAddSessions = group.sessions.filter((session) =>
    isPendingAddSession(session, group)
  );
  const pendingAddIds = new Set(pendingAddSessions.map((session) => session.id));
  const visibleSessions = group.sessions.filter(
    (session) => !isEndedSession(session) && !pendingAddIds.has(session.id)
  );
  const hasPendingSessionChanges =
    pendingAddSessions.length > 0 || pendingRemoveSessions.length > 0;

  return (
    <>
      <Card>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Jurisdiction" value={emptyDash(jurisdictionNames.join(', '))} />
            <DetailField
              label="Accepting new patients"
              value={
                <Pill
                  size="sm"
                  variant="subtle"
                  color={group.accepting_new_patients ? 'primary' : 'neutral'}>
                  {group.accepting_new_patients ? 'Yes' : 'No'}
                </Pill>
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Appointment type" value={emptyDash(group.appointment_type_name)} />
            <DetailField label="Timezone" value={emptyDash(group.timezone)} />
          </div>
          <DetailField label="Capacity" value={String(group.capacity)} />
          <div className="grid grid-cols-2 gap-4">
            <DetailField
              label="Effective date"
              value={formatOptionalCalendarDate(group.start_date)}
            />
            <DetailField
              label="End date"
              value={group.end_date ? formatCalendarDate(group.end_date) : 'Ongoing'}
            />
          </div>
        </div>
      </Card>

      <EligibilityCriteriaBox excludeGroup={isJurisdictionGroup} subjectId={group.id} />

      <section>
        <Typography variant="label" muted className="mb-2">
          Standing schedule
        </Typography>

        <div className="space-y-2">
          {visibleSessions.map((session) => (
            <SessionCard key={session.id} memberFirstName={memberFirstName} session={session} />
          ))}
        </div>

        {hasPendingSessionChanges && (
          <div className="mt-4 rounded-lg border border-dashed border-uniform-gray-300 p-3 dark:border-uniform-gray-600">
            <div className="mb-2 flex items-center justify-between">
              <Typography variant="label" muted className="mb-0">
                Upcoming changes
              </Typography>
              <Pill size="sm" variant="subtle" color="warning">
                Effective {formatOptionalCalendarDate(getNextMonday())}
              </Pill>
            </div>
            <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-2 gap-y-2">
              {pendingAddSessions.map((session) => (
                <Fragment key={session.id}>
                  <Pill size="sm" color="primary">
                    + Adding
                  </Pill>
                  <SessionRow session={session} />
                </Fragment>
              ))}
              {pendingRemoveSessions.map((session) => (
                <Fragment key={session.id}>
                  <Pill size="sm" color="destructive">
                    − Removing
                  </Pill>
                  <SessionRow session={session} />
                </Fragment>
              ))}
            </div>
          </div>
        )}

        {endedSessions.length > 0 && (
          <div className="mt-4 border-t border-uniform-gray-200 pt-3 dark:border-uniform-gray-700">
            <Typography variant="label" muted className="mb-2">
              Ended sessions
            </Typography>
            <div className="grid grid-cols-4 items-center gap-x-2 gap-y-2 opacity-60">
              {endedSessions.map((session) => (
                <Fragment key={session.id}>
                  <SessionRow session={session} />
                  <Typography variant="body">
                    {formatOptionalCalendarDate(session.effective_until)}
                  </Typography>
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </section>

      <UpcomingAppointmentsList
        occurrences={group.upcoming_group_session_occurrences ?? []}
        timezone={timezone}
      />

      <TreatmentGroupMembersList group={group} memberActions={memberActions} />
    </>
  );
};

export default TreatmentGroupDetailSections;
